/**
 * Claims & LD dashboards with unlimited drill-down.
 *
 * Every chart is a drill surface: click a bar, it pins that value and asks for
 * the next dimension, down to the individual record. One generic backend
 * endpoint serves every level, so the breadcrumb is the only state we keep.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ChevronRight, Loader2, TrendingDown, Clock, Gavel, Timer, RefreshCw, Layers,
} from 'lucide-react';

const fmt = (v, d = 0) =>
  v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: d });

const compact = (v) => {
  if (v == null) return '—';
  const n = Math.abs(v);
  if (n >= 1e7) return `${(v / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${(v / 1e5).toFixed(2)} L`;
  return fmt(v);
};

/** Registers store SCREAMING_SNAKE enums; a dashboard should not shout back. */
const ACRONYMS = new Set(['EOT', 'LD', 'VO', 'NEC', 'FIDIC', 'AI', 'EPC']);
const pretty = (v) => String(v || '—').split('_')
  .map(w => (ACRONYMS.has(w.toUpperCase())
    ? w.toUpperCase()
    : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
  .join(' ');

const Kpi = ({ label, value, sub, tone = '', testId }) => (
  <div data-testid={testId}
       className="rounded-xl px-3.5 py-2.5 bg-card border border-border shadow-sm
                  ring-1 ring-inset ring-white/[0.03] transition-colors hover:border-sky-500/40">
    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={`text-xl font-semibold tabular-nums ${tone}`}>{value}</p>
    {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const Card = ({ title, action, children, testId, className = '' }) => (
  <div data-testid={testId}
       className={`rounded-xl bg-card border border-border shadow-sm overflow-hidden ${className}`}>
    <div className="flex items-center gap-2 px-3.5 py-2 border-b border-border bg-muted/40">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{title}</p>
      <span className="ml-auto flex items-center">{action}</span>
    </div>
    <div className="p-3.5">{children}</div>
  </div>
);

/** Horizontal bar list — the universal drill affordance. */
const Bars = ({ rows, labelKey = 'label', valueKey = 'value', onPick, format = compact, testIdPrefix }) => {
  const max = Math.max(1, ...rows.map(r => Math.abs(r[valueKey] || 0)));
  return (
    <div className="space-y-1">
      {rows.map((r, i) => (
        <button key={r.key ?? i} onClick={() => onPick?.(r)} disabled={!onPick}
                className={`w-full text-left group ${onPick ? 'cursor-pointer' : 'cursor-default'}`}
                data-testid={`${testIdPrefix}-${r.key ?? i}`}>
          <div className="flex items-center justify-between text-[11px] mb-0.5">
            <span className="truncate pr-2 group-hover:text-sky-400 transition-colors">
              {r[labelKey]}
              {r.count != null && <span className="text-muted-foreground"> · {r.count}</span>}
            </span>
            <span className="tabular-nums shrink-0">{format(r[valueKey])}</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-sky-500 to-violet-500 transition-all"
                 style={{ width: `${(Math.abs(r[valueKey] || 0) / max) * 100}%` }} />
          </div>
        </button>
      ))}
      {!rows.length && <p className="text-[11px] text-muted-foreground">Nothing to show.</p>}
    </div>
  );
};

/** Waterfall rendered as signed bars so the walk reads top to bottom. */
const Waterfall = ({ rows, testId }) => {
  const max = Math.max(1, ...rows.map(r => Math.abs(r.value || 0)));
  return (
    <div className="space-y-1.5" data-testid={testId}>
      {rows.map((r, i) => (
        <div key={i}>
          <div className="flex justify-between text-[11px]">
            <span className={r.kind === 'total' ? 'font-medium' : 'text-muted-foreground'}>{r.label}</span>
            <span className="tabular-nums">{compact(r.value === 0 ? 0 : r.value)}</span>
          </div>
          <div className="h-2 rounded bg-muted overflow-hidden">
            <div className={`h-full rounded ${r.kind === 'total' ? 'bg-sky-500'
              : r.value < 0 ? 'bg-red-500/70' : 'bg-emerald-500/70'}`}
                 style={{ width: `${(Math.abs(r.value || 0) / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

/** Drill panel — breadcrumb + current grouping + leaf records. */
const DrillPanel = ({ dataset, projectId, contractId, seed, testId }) => {
  const [crumbs, setCrumbs] = useState(seed ? [seed] : []);
  const [groupBy, setGroupBy] = useState(dataset === 'claims' ? 'cause' : 'type');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = crumbs.map(c => `${c.dim}=${c.value}`).join(';');
      const { data } = await api.get('/contracts-dash/drill', {
        params: { dataset, groupBy: groupBy || '', filters,
                  project_id: projectId, contract_id: contractId },
      });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Drill-down failed');
    } finally { setLoading(false); }
  }, [dataset, groupBy, crumbs, projectId, contractId]);

  useEffect(() => { load(); }, [load]);

  const dimLabel = (k) => data?.dimensions?.find(d => d.key === k)?.label || k;

  return (
    <Card title={`Drill-down — ${dataset === 'claims' ? 'claims' : 'LD'}`} testId={testId}
          action={loading ? <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /> : (
            <button onClick={load} className="text-muted-foreground hover:text-slate-200"
                    data-testid={`${testId}-refresh`}>
              <RefreshCw className="w-3 h-3" />
            </button>
          )}>
      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-1 mb-2 text-[10px]">
        <button onClick={() => { setCrumbs([]); setGroupBy(dataset === 'claims' ? 'cause' : 'type'); }}
                className="text-sky-500" data-testid={`${testId}-crumb-all`}>All</button>
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1">
            <ChevronRight className="w-3 h-3 text-muted-foreground" />
            <button className="text-foreground"
                    onClick={() => setCrumbs(crumbs.slice(0, i + 1))}
                    data-testid={`${testId}-crumb-${i}`}>
              {dimLabel(c.dim)}: {c.label}
            </button>
          </span>
        ))}
        <span className="ml-auto text-muted-foreground">
          {data?.totalRecords ?? 0} {data?.totalRecords === 1 ? 'record' : 'records'}
        </span>
      </div>

      {/* Dimension picker */}
      <div className="flex flex-wrap gap-1 mb-2">
        {(data?.dimensions || [])
          .filter(d => !crumbs.some(c => c.dim === d.key))
          .map(d => (
            <button key={d.key} onClick={() => setGroupBy(d.key)}
                    className={`px-2 py-0.5 rounded-full border text-[10px] ${
                      groupBy === d.key ? 'border-sky-500 text-sky-400 bg-sky-500/10'
                                        : 'border-border text-muted-foreground'}`}
                    data-testid={`${testId}-dim-${d.key}`}>
              {d.label}
            </button>
          ))}
        <button onClick={() => setGroupBy('')}
                className={`px-2 py-0.5 rounded-full border text-[10px] ${
                  !groupBy ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10'
                           : 'border-border text-muted-foreground'}`}
                data-testid={`${testId}-dim-records`}>
          Records
        </button>
      </div>

      {data?.leaf ? (
        <ScrollArea className="h-56">
          <table className="w-full text-[11px]">
            <thead className="text-[9px] uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left py-1">Ref</th><th className="text-left">Title</th>
                <th className="text-left w-28">Status</th><th className="text-right w-28">Value</th></tr>
            </thead>
            <tbody>
              {(data.records || []).map(r => (
                <tr key={r.id} className="border-t border-border"
                    data-testid={`${testId}-record-${r.id}`}>
                  <td className="py-1 font-mono text-[10px]">{r.ref}</td>
                  <td className="py-1 truncate max-w-[18rem]">{r.title}</td>
                  <td className="py-1">
                    {r.status && <Badge variant="outline" className="text-[9px]">{pretty(r.status)}</Badge>}
                  </td>
                  <td className="py-1 text-right tabular-nums">{compact(r.value)}</td>
                </tr>
              ))}
              {!data.records?.length && (
                <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">No records here.</td></tr>
              )}
            </tbody>
          </table>
        </ScrollArea>
      ) : (
        <ScrollArea className="h-56 pr-2">
          <Bars rows={(data?.groups || []).map(g => ({
                  ...g, label: data.groupedBy === 'contract' || data.groupedBy === 'month'
                    ? g.label : pretty(g.label) }))}
                testIdPrefix={`${testId}-group`}
                onPick={(g) => {
                  setCrumbs([...crumbs, { dim: data.groupedBy, value: g.key, label: g.label }]);
                  const next = (data.remaining || []).find(k => k !== data.groupedBy);
                  setGroupBy(next || '');
                }} />
        </ScrollArea>
      )}
      <p className="text-[9px] text-muted-foreground mt-1.5">
        Click a bar to drill in · pick a dimension to regroup · "Records" shows the items themselves
      </p>
    </Card>
  );
};

// ══════════════════════════════════════════════════════════════════════
export const ClaimsDashboard = ({ projectId, contractId }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/contracts-dash/claims',
          { params: { project_id: projectId, contract_id: contractId } });
        setD(data);
      } catch { toast.error('Could not load the claims dashboard'); }
      finally { setLoading(false); }
    })();
  }, [projectId, contractId]);

  if (loading) return <div className="h-72 loading-shimmer rounded-xl" />;
  if (!d) return null;
  const k = d.kpis;

  return (
    <div className="space-y-3" data-testid="claims-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2">
        <Kpi label="Claims" value={fmt(k.total)} sub={`${k.open} open · ${k.closed} closed`} testId="claims-kpi-total" />
        <Kpi label="Claimed" value={compact(k.requestedValue)} sub={`${fmt(k.requestedDays, 1)} days`} testId="claims-kpi-claimed" />
        <Kpi label="Open exposure" value={compact(k.openValue)} tone="text-amber-500" testId="claims-kpi-open" />
        <Kpi label="Settled" value={compact(k.approvedValue)} sub={`${fmt(k.approvedDays, 1)} days granted`} tone="text-emerald-500" testId="claims-kpi-settled" />
        <Kpi label="Recovery rate" value={k.recoveryRatePct == null ? '—' : `${k.recoveryRatePct}%`}
             sub="of decided claims" testId="claims-kpi-recovery" />
        <Kpi label="Avg age" value={`${fmt(k.avgAgeDays, 0)}d`}
             sub={k.timeBarFlags ? `${k.timeBarFlags} time-bar flags` : 'no time-bar flags'}
             tone={k.timeBarFlags ? 'text-red-500' : ''} testId="claims-kpi-age" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Claimed → settled → still open" testId="claims-waterfall">
          <Waterfall rows={d.waterfall || []} testId="claims-waterfall-rows" />
        </Card>
        <Card title="Ageing of the open book" testId="claims-ageing">
          <Bars rows={(d.ageing || []).map(a => ({ key: a.bucket, label: `${a.bucket} days`, value: a.value, count: a.count }))}
                testIdPrefix="claims-age" />
        </Card>
        <Card title="Cause Pareto" testId="claims-pareto">
          <Bars rows={(d.causePareto || []).map(p => ({
                  key: p.cause,
                  label: p.value ? `${pretty(p.cause)} (${p.cumulativePct}% cum.)` : pretty(p.cause),
                  value: p.value, count: p.count }))}
                testIdPrefix="claims-cause" />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Status mix" testId="claims-status">
          <Bars rows={(d.statusMix || []).map(s => ({ key: s.status, label: pretty(s.status), value: s.count }))}
                format={fmt} testIdPrefix="claims-status" />
        </Card>
        <Card title="Exposure by contract" testId="claims-heat">
          <ScrollArea className="h-44 pr-2">
            <Bars rows={(d.contractHeat || []).map(c => ({ key: c.contractId, label: c.contract, value: c.requested, count: c.count }))}
                  testIdPrefix="claims-contract" />
          </ScrollArea>
        </Card>
        <Card title="Raised vs settled by month" testId="claims-trend">
          <Bars rows={(d.trend || []).map(t => ({ key: t.month, label: t.month, value: t.value, count: t.raised }))}
                testIdPrefix="claims-month" />
        </Card>
      </div>

      {d.timeBarWatchlist?.length > 0 && (
        <Card title="Time-bar watchlist — flagged by Cognara AI" testId="claims-timebar">
          <div className="space-y-1 text-[11px]">
            {d.timeBarWatchlist.map(t => (
              <div key={t.id} className="flex items-start gap-2 border-t border-border pt-1">
                <Badge variant="outline"
                       className={`text-[9px] shrink-0 ${t.status === 'BREACHED' ? 'text-red-500' : 'text-amber-500'}`}>
                  {t.status}
                </Badge>
                <div>
                  <p>{t.claim} — {t.title}</p>
                  <p className="text-[10px] text-muted-foreground">{t.comment}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <DrillPanel dataset="claims" projectId={projectId} contractId={contractId}
                  testId="claims-drill" />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
export const LDDashboard = ({ projectId, contractId }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/contracts-dash/ld',
          { params: { project_id: projectId, contract_id: contractId } });
        setD(data);
      } catch { toast.error('Could not load the LD dashboard'); }
      finally { setLoading(false); }
    })();
  }, [projectId, contractId]);

  if (loading) return <div className="h-72 loading-shimmer rounded-xl" />;
  if (!d) return null;
  const k = d.kpis;

  return (
    <div className="space-y-3" data-testid="ld-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2">
        <Kpi label="Trackers" value={fmt(k.trackers)} sub={`${k.atCap} at cap`} testId="ld-kpi-trackers" />
        <Kpi label="Current exposure" value={compact(k.currentExposure)} tone="text-amber-500" testId="ld-kpi-current" />
        <Kpi label="Forecast at completion" value={compact(k.forecastExposure)} tone="text-red-500" testId="ld-kpi-forecast" />
        <Kpi label="Cap utilisation"
             value={k.capUtilisationPct == null ? '—' : `${k.capUtilisationPct}%`}
             sub={`cap ${compact(k.totalCap)}`} testId="ld-kpi-cap" />
        <Kpi label="Delay days" value={fmt(k.delayDays, 1)}
             sub={k.notRecalculated
               ? `${k.notRecalculated} tracker(s) never recalculated`
               : `${fmt(k.chargeableDays, 1)} chargeable · ${fmt(k.excusableDays, 1)} excusable`}
             tone={k.notRecalculated ? 'text-amber-500' : ''}
             testId="ld-kpi-days" />
        <Kpi label="Next milestone"
             value={k.nextMilestoneDays == null ? '—'
               : k.nextMilestoneDays < 0 ? `${Math.abs(k.nextMilestoneDays)}d late` : `${k.nextMilestoneDays}d`}
             sub={`${k.milestonesAtRisk} forecast to slip`}
             tone={k.nextMilestoneDays < 0 ? 'text-red-500' : ''} testId="ld-kpi-next" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Gross delay → current exposure" testId="ld-waterfall">
          <Waterfall rows={d.waterfall || []} testId="ld-waterfall-rows" />
        </Card>
        <Card title="Cap utilisation bands" testId="ld-bands">
          <Bars rows={(d.capBands || []).map(b => ({ key: b.band, label: b.band, value: b.count }))}
                format={fmt} testIdPrefix="ld-band" />
        </Card>
        <Card title="Forecast exposure, next 12 months" testId="ld-projection">
          <Bars rows={(d.projection || []).map(p => ({ key: p.month, label: p.month, value: p.exposure }))}
                testIdPrefix="ld-proj" />
        </Card>
      </div>

      <Card title="Milestone countdown" testId="ld-countdown">
        <ScrollArea className="h-56">
          <table className="w-full text-[11px]">
            <thead className="text-[9px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left py-1">Milestone</th>
                <th className="text-left">Contract</th>
                <th className="text-right w-24">Contractual</th>
                <th className="text-right w-24">Forecast</th>
                <th className="text-right w-20">Slip</th>
                <th className="text-right w-24">Rate/day</th>
                <th className="text-right w-28">Forecast LD</th>
                <th className="text-right w-20">% cap</th>
              </tr>
            </thead>
            <tbody>
              {(d.countdown || []).map(c => (
                <tr key={c.id} className="border-t border-border" data-testid={`ld-countdown-${c.id}`}>
                  <td className="py-1">{c.milestone}</td>
                  <td className="py-1 truncate max-w-[14rem] text-muted-foreground">{c.contract}</td>
                  <td className="py-1 text-right tabular-nums">{String(c.contractualDate).slice(0, 10)}</td>
                  <td className="py-1 text-right tabular-nums">
                    {c.forecastDate ? String(c.forecastDate).slice(0, 10) : '—'}
                  </td>
                  <td className={`py-1 text-right tabular-nums ${c.forecastSlipDays > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                    {c.forecastSlipDays == null ? '—' : `${c.forecastSlipDays > 0 ? '+' : ''}${c.forecastSlipDays}d`}
                  </td>
                  <td className="py-1 text-right tabular-nums">{compact(c.dailyRate)}</td>
                  <td className="py-1 text-right tabular-nums">{compact(c.forecastExposure)}</td>
                  <td className={`py-1 text-right tabular-nums ${c.capReached ? 'text-red-500' : ''}`}>
                    {fmt(c.percentOfCap, 1)}%
                  </td>
                </tr>
              ))}
              {!d.countdown?.length && (
                <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">
                  No LD trackers with a contractual date yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </ScrollArea>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Exposure by contract" testId="ld-rollup">
          <ScrollArea className="h-44 pr-2">
            <Bars rows={(d.contractRollup || []).map(c => ({ key: c.contractId, label: c.contract, value: c.forecast, count: c.trackers }))}
                  testIdPrefix="ld-contract" />
          </ScrollArea>
        </Card>
        <DrillPanel dataset="ld" projectId={projectId} contractId={contractId} testId="ld-drill" />
      </div>
    </div>
  );
};

export default ClaimsDashboard;
